import {
  createContext,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { ChevronDown } from "lucide-react";
import { Icon } from "../Icon/Icon.js";
import { useMenuDismiss } from "../Menu/useMenuDismiss.js";
import styles from "./AccountMenu.module.scss";

/**
 * The account chip in a shell's top bar, and the menu it opens — the one "who am I / get me out of here"
 * control every shell paints in the same corner.
 *
 * It exists because three shells had hand-rolled it (the artist band switcher, the roster account chip, the
 * org chip) and the copies had drifted in exactly the way the component-library note in CLAUDE.md warns
 * about: the artist one carried the mono section-head ruling, the red destructive last item and a dismiss
 * contract; the other two carried a black "Sign out", a different panel radius and NO dismiss at all — their
 * menus could only be closed by choosing something, and Escape did nothing. The drift was never cosmetic.
 *
 * It is NOT a `MenuButton`. That one's popup is a flat list of label+icon actions; this one's rows carry an
 * avatar, a role sub-line and a current-selection marker, and its trigger is an identity, not a verb. What
 * the two genuinely share is the dismiss contract, and that is exactly what this imports.
 */
export interface AccountMenuProps {
  /**
   * The chip's leading mark. Pass an `<Avatar size="xs">` — `rounded` for a band or an org, `circle` for a
   * person (the shape is what says which). A caller resolving an authenticated media URL passes its own
   * component wrapping one, which is why this is a node rather than an initials string.
   */
  avatar: ReactNode;
  /** The chip's primary line: the band, the person, the org. */
  name: string;
  /** Optional second line under the name ("Label · Admin"). */
  sub?: string;
  /** The menu's accessible name (`aria-label` on the list) — say whose account it is. */
  menuLabel: string;
  /** The menu's contents: the `AccountMenu*` parts below, in order. */
  children: ReactNode;
  className?: string;
}

/** Closes the open menu. Every part calls it BEFORE running its action, so an action that opens a dialog
 *  does not leave the panel painted underneath it (the `ActionMenu` rule). */
const CloseContext = createContext<() => void>(() => {});

function useClose(): () => void {
  return useContext(CloseContext);
}

export function AccountMenu({ avatar, name, sub, menuLabel, children, className }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const close = useCallback(() => setOpen(false), []);
  // Outside-press closes, Escape closes and returns focus to the chip, and opening moves focus to the first
  // item. The trigger sits INSIDE the root, so pressing it while open is a toggle, not an outside press
  // that would close and immediately re-open.
  useMenuDismiss(open, close, rootRef, triggerRef);

  return (
    <div className={clsx(styles.root, className)} ref={rootRef}>
      {/* No `aria-label`: the chip's accessible name is the identity it displays. A label here would
          replace the visible name with chrome wording, which is the WCAG 2.5.3 mismatch. */}
      <button
        ref={triggerRef}
        type="button"
        className={styles.chip}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {avatar}
        <span className={styles.chipText}>
          <span className={styles.chipName}>{name}</span>
          {sub && <span className={styles.chipSub}>{sub}</span>}
        </span>
        <span className={styles.chipChev}>
          <Icon icon={ChevronDown} size={16} />
        </span>
      </button>
      {open && (
        <CloseContext.Provider value={close}>
          <ul id={menuId} className={styles.menu} role="menu" aria-label={menuLabel}>
            {children}
          </ul>
        </CloseContext.Provider>
      )}
    </div>
  );
}

/** The panel's opening row: a label over a mono meta line, and the door behind it (the signed-in address
 *  under "View my profile"). An optional leading `avatar` (the caller's own face, resolved through the
 *  authenticated avatar route — so a node, like the chip's mark) puts the profile picture beside it (ART-238),
 *  which is what the mobile account sheet already does. */
export function AccountMenuHeader({
  label,
  meta,
  avatar,
  onSelect,
}: {
  label: string;
  meta?: string | null;
  /** The caller's own avatar node (`<Avatar>`/`<PersonAvatar>`). Omit it and the row is label-over-meta. */
  avatar?: ReactNode;
  onSelect: () => void;
}) {
  const close = useClose();
  return (
    <li role="none">
      <button
        type="button"
        role="menuitem"
        className={styles.header}
        onClick={() => {
          close();
          onSelect();
        }}
      >
        {avatar}
        <span className={styles.headerBody}>
          <span className={styles.headerLabel}>{label}</span>
          {meta && <span className={styles.headerMeta}>{meta}</span>}
        </span>
      </button>
    </li>
  );
}

/** A section label ("YOUR BANDS"). Mono tracked caps — a section label is chrome, and chrome is mono
 *  (STYLE-GUIDE ruling 8). Not focusable, so it is `role="none"` inside the menu. */
export function AccountMenuSection({ children }: { children: ReactNode }) {
  return (
    <li role="none" className={styles.section}>
      {children}
    </li>
  );
}

export function AccountMenuDivider() {
  return <li role="none" className={styles.divider} />;
}

/**
 * A plain text entry. `tone="danger"` is the panel's LAST WORD — Log out — and it is red because leaving is
 * the one thing in here you cannot undo with another click.
 *
 * Passing `checked` makes it a `menuitemradio`: one of a set you are choosing between (which organization
 * you are acting for), rather than an action.
 */
export function AccountMenuItem({
  children,
  onSelect,
  tone = "default",
  checked,
}: {
  children: ReactNode;
  onSelect: () => void;
  tone?: "default" | "danger";
  checked?: boolean;
}) {
  const close = useClose();
  return (
    <li role="none">
      <button
        type="button"
        role={checked === undefined ? "menuitem" : "menuitemradio"}
        aria-checked={checked}
        className={clsx(styles.item, tone === "danger" && styles.itemDanger)}
        onClick={() => {
          close();
          onSelect();
        }}
      >
        {children}
      </button>
    </li>
  );
}

/**
 * A rich row: avatar · name over a role sub-line · an optional trailing marker. The band switcher's list,
 * where a row is an identity you are switching INTO rather than a command.
 */
export function AccountMenuRow({
  avatar,
  name,
  meta,
  mark,
  checked,
  onSelect,
  trailing,
}: {
  avatar: ReactNode;
  name: string;
  /** The mono secondary line — a string, or a node (a `RoleLabel` with the AV mark) for a seat row. */
  meta?: ReactNode;
  /** Trailing marker on the current row ("CURRENT"). */
  mark?: string;
  checked?: boolean;
  onSelect: () => void;
  /**
   * A second control at the row's trailing edge (the band switcher's star, ART-319). Rendered BESIDE the row's
   * button, never inside it — a button in a button is invalid markup — and overlaid on its right end, which
   * reserves the room for it. Its own clicks are its own: it does not select the row or close the menu.
   */
  trailing?: ReactNode;
}) {
  const close = useClose();
  return (
    <li role="none" className={trailing ? styles.rowItem : undefined}>
      <button
        type="button"
        role={checked === undefined ? "menuitem" : "menuitemradio"}
        aria-checked={checked}
        data-trailing={trailing ? "" : undefined}
        className={clsx(styles.row, checked && styles.rowCurrent)}
        onClick={() => {
          close();
          onSelect();
        }}
      >
        {avatar}
        <span className={styles.rowBody}>
          <span className={styles.rowName}>{name}</span>
          {meta && <span className={styles.rowMeta}>{meta}</span>}
        </span>
        {mark && <span className={styles.rowMark}>{mark}</span>}
      </button>
      {trailing && <span className={styles.rowTrailing}>{trailing}</span>}
    </li>
  );
}
